<template>
    <v-row justify="center">
        <v-dialog v-model="show_booking_checklist_dialog" max-width="760" persistent scrollable>
            <v-card>
                <v-card-title>Booking Checklist</v-card-title>
                <v-card-text>
                    <p class="grey--text text--darken-1 mb-4">
                        Every event of {{ get_selected_company.company_name }} shows this list on its Checklist tab.
                        Removing a line takes it off events that haven't ticked or assigned it; events that have keep it.
                        Tasks for one event only are added on that event's Checklist tab.
                    </p>
                    <div class="d-flex mb-4" style="gap:12px;">
                        <v-text-field v-model="new_item.name" label="New checklist item *" outlined dense hide-details
                            @keyup.enter="createItem()"></v-text-field>
                        <v-text-field v-model="new_item.description" label="Notes" outlined dense
                            hide-details></v-text-field>
                        <v-btn dark color="#3988AA" class="custom-buttons" :loading="saving" @click="createItem()">Add</v-btn>
                    </div>
                    <v-skeleton-loader v-if="loading" type="list-item@5"></v-skeleton-loader>
                    <div v-else-if="items.length === 0" class="pa-6 text-center grey--text">No checklist items yet</div>
                    <v-card v-else outlined>
                        <div v-for="(item, index) in items" :key="item.id" class="checklist-setting-row">
                            <div class="d-flex flex-column">
                                <v-btn icon x-small :disabled="index === 0 || saving" @click="move(index, -1)">
                                    <v-icon small>mdi-chevron-up</v-icon>
                                </v-btn>
                                <v-btn icon x-small :disabled="index === items.length - 1 || saving" @click="move(index, 1)">
                                    <v-icon small>mdi-chevron-down</v-icon>
                                </v-btn>
                            </div>
                            <template v-if="editing && editing.id === item.id">
                                <v-text-field v-model="editing.name" label="Item *" outlined dense hide-details
                                    class="mr-2"></v-text-field>
                                <v-text-field v-model="editing.description" label="Notes" outlined dense hide-details
                                    class="mr-2"></v-text-field>
                                <v-btn icon small color="#2E7D32" :loading="saving" @click="saveEdit()"><v-icon>mdi-check</v-icon></v-btn>
                                <v-btn icon small @click="editing = null"><v-icon>mdi-close</v-icon></v-btn>
                            </template>
                            <template v-else>
                                <div class="flex-grow-1">
                                    <div class="font-weight-bold">{{ item.name }}</div>
                                    <div v-if="item.description" class="grey--text" style="font-size:12px;">{{ item.description }}</div>
                                </div>
                                <v-btn icon small @click="editing = { ...item, description: item.description || '' }">
                                    <v-icon small>mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-btn icon small color="red" @click="deleteItem(item)">
                                    <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                            </template>
                        </div>
                    </v-card>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="custom-buttons" text @click="$emit('closeBookingChecklistDialog')">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    props: ['show_booking_checklist_dialog'],
    data: () => ({
        loading: false,
        saving: false,
        items: [],
        new_item: { name: '', description: '' },
        editing: null,
    }),
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
    },
    watch: {
        show_booking_checklist_dialog(open) {
            if (open) this.getItems()
        },
    },
    methods: {
        async getItems() {
            this.loading = true
            try {
                const result = (await this.$axios.get(`admin/checklists/get_items/${this.get_selected_company.id}`)).data
                if (result.response == true) this.items = result.data
            } finally {
                this.loading = false
            }
        },
        async request(fn, failMessage) {
            this.saving = true
            try {
                const result = (await fn()).data
                if (result.response == true) return result
                this.$toast.error(result.message || failMessage)
            } catch (e) {
                this.$toast.error(e.response?.data?.message || failMessage)
            } finally {
                this.saving = false
            }
            return null
        },
        async createItem() {
            if (!this.new_item.name.trim()) {
                this.$toast.error('Enter the checklist item')
                return
            }
            const result = await this.request(() => this.$axios.post('admin/checklists/create_item', {
                company_id: this.get_selected_company.id, ...this.new_item,
            }), 'Failed to add the item')
            if (result) {
                this.items.push(result.data)
                this.new_item = { name: '', description: '' }
            }
        },
        async saveEdit() {
            if (!this.editing.name.trim()) {
                this.$toast.error('Enter the checklist item')
                return
            }
            const result = await this.request(() => this.$axios.post(`admin/checklists/update_item/${this.editing.id}`, {
                name: this.editing.name, description: this.editing.description,
            }), 'Failed to update the item')
            if (result) {
                const index = this.items.findIndex(i => i.id === result.data.id)
                this.items.splice(index, 1, result.data)
                this.editing = null
            }
        },
        async deleteItem(item) {
            if (!confirm(`Remove "${item.name}" from the checklist?`)) return
            const result = await this.request(() => this.$axios.delete(`admin/checklists/delete_item/${item.id}`), 'Failed to delete the item')
            if (result) this.items = this.items.filter(i => i.id !== item.id)
        },
        async move(index, step) {
            const items = this.items.slice()
            const [moved] = items.splice(index, 1)
            items.splice(index + step, 0, moved)
            const result = await this.request(() => this.$axios.post(`admin/checklists/reorder_items/${this.get_selected_company.id}`, {
                ids: items.map(i => i.id),
            }), 'Failed to reorder')
            if (result) this.items = items
        },
    },
}
</script>

<style lang="scss" scoped>
.checklist-setting-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid #F0F0F0;

    &:last-child {
        border-bottom: none;
    }
}
</style>
